import React, {useEffect} from 'react';
import {Icon, Table} from "antd";
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';
import {withRouter} from 'react-router-dom';

import InterventionStatus from '../../common/format/InterventionStatus';
import JourneyType from '../../common/format/JourneyType';
import MessageStatus from '../../common/format/MessageStatus';
import InterventionType from '../../intervention/InterventionType';
import InterventionDate from '../../common/format/InterventionDate';
import InterventionCustomer from '../../common/format/InterventionCustomer';
import InterventionVehicle from '../../common/format/InterventionVehicle';
import DateFormat from '../../common/format/DateFormat';
import {isOnAlert} from '../../../services/Tasks';
import Constants from '../../../services/Constants';
import Intervention from '../../../services/Intervention';
import User from '../../../services/User';
import Storage from '../../../services/Storage';
import {getDealersIdsAndName} from '../../../selectors/dealers';
import {getFilters} from "../../../selectors/interventionsSelector";
import {fetchDealers} from '../../../actions/dealersActions';
import {LOCATION_KEY, PREVIOUS_LOCATION, SCROLL_POSITION} from '../../../constants/storageKeys';

import FilterDate from './filter/FilterDate';
import FilterCheckbox from './filter/FilterCheckbox';
import './TasksTable.css';

const Column = Table.Column;

const TasksTable = ({fetchInterventions: {interventions, loading}, fetchDistinctAdvisors: {distinctAdvisors}, contentUpdater, dealers, filters, showArchived, location, history, dispatchFetchAdvisors, dispatchFetchDealers}) => {

  const intl = useIntl();

  useEffect(() => {
    if (!User.isCallCenterAgent()) {
      dispatchFetchAdvisors();
    } else {
      dispatchFetchDealers();
    }
  }, []);

  const parseDealersToFilterLabel = (dealersList) => {
    if (!dealersList) {
      return [{label: '', value: ''}];
    }
    return dealersList.map(dealer => ({label: dealer.dealerName, value: dealer.id}));
  };

  const parseAdvisorsToFilterLabel = (distinctAdvisorsList) => {
    if (!distinctAdvisorsList) {
      return [{label: '', value: ''}];
    }
    return distinctAdvisorsList.map(advisor => ({label: advisor, value: advisor}));
  };

  const persistScrollPositionAndLocation = () => {
    Storage.setSessionItem(SCROLL_POSITION, window.scrollY);
    if (location.key) {
      Storage.setSessionItem(LOCATION_KEY, location.key);
    } else {
      Storage.setSessionItem(PREVIOUS_LOCATION, location.pathname);
    }
  };

  const handleRowClick = (record) => {
    if (!User.isCallCenterAgent()) {
      persistScrollPositionAndLocation();
      history.push(`/interventions/${record.interventionId}`);
    }
  };

  const getRowClassName = (record) => {
    if (!Intervention.requiresRedFlag(record)) {
      return null;
    }
    const startDate = new Date(record.interventionStart);
    const endDate = new Date(record.interventionEnd);
    return isOnAlert(startDate, endDate, record.interventionStatus, record.type) ? "is-on-alert" : null;
  };

  const statusFilterOptions = Constants.STATUS_FILTER.map(status => ({
    label: intl.formatMessage(status.label),
    value: status.value
  }));
  if (showArchived) {
    statusFilterOptions.push({
      label: intl.formatMessage(Constants.STATUS_ARCHIVED.label),
      value: Constants.STATUS_ARCHIVED.value
    });
  }

  return (
    <div className="table-wrapper__body">
      <Table
        className="table overview-table"
        dataSource={interventions.content}
        loading={loading}
        rowKey="interventionId"
        pagination={false}
        rowClassName={getRowClassName}
        onRow={(record) => ({onClick: () => handleRowClick(record)})}
      >
        {User.isCallCenterAgent() && (
          <Column
            title={intl.formatMessage({id: 'menu.dealership'})}
            dataIndex="dealerName"
            key="dealerName"
            className="overview-table__dealerName"
            filterDropdown={(filter) => (
              <FilterCheckbox apiKey="dealerId" contentUpdater={contentUpdater} options={parseDealersToFilterLabel(dealers)} filter={filter}/>
            )}
            filterIcon={() => {
              const isFiltered = filters.dealerId && filters.dealerId.length > 0;
              return <Icon type='filter' theme={isFiltered ? 'filled' : 'outlined'} style={{color: isFiltered ? '#1C69D4' : '#BFBFBF'}}/>;
            }}
          />
        )}
        {User.isCallCenterAgent() && (
          <Column
            title={intl.formatMessage({id: 'tasksTable.createdOn'})}
            dataIndex="createdOn"
            key="createOn"
            className="overview-table__createdOn"
            render={(text) => <DateFormat date={text} layout="dateOnly"/>}
            filterDropdown={(filter) => (
              <FilterDate apiKeyStart="createdOnFrom" apiKeyEnd="createdOnTo" contentUpdater={contentUpdater} filter={filter}/>
            )}
            filterIcon={() => {
              const isFiltered = (typeof filters.createdOnFrom === 'string' && filters.createdOnFrom.length > 0) || (typeof filters.createdOnTo === 'string' && filters.createdOnTo.length > 0);
              return <Icon type='filter' theme={isFiltered ? 'filled' : 'outlined'} style={{color: isFiltered ? '#1C69D4' : '#BFBFBF'}}/>;
            }}
          />
        )}
        {!User.isCallCenterAgent() && (
          <Column
            title={intl.formatMessage({id: 'tasksTable.status'})}
            dataIndex="interventionStatus"
            key="status"
            className="overview-table__status"
            filterDropdown={(filter) => (
              <FilterCheckbox apiKey="interventionStatus" contentUpdater={contentUpdater} options={statusFilterOptions} filter={filter}/>
            )}
            filterIcon={() => {
              const isFiltered = filters.interventionStatus && filters.interventionStatus.length > 0;
              return <Icon type='filter' theme={isFiltered ? 'filled' : 'outlined'} style={{color: isFiltered ? '#1C69D4' : '#BFBFBF'}}/>;
            }}
            render={(text, record) => (
              <InterventionStatus status={text}/>
            )}
          />
        )}
        {!User.isCallCenterAgent() && (
          <Column
            title={intl.formatMessage({id: 'tasksTable.createdOn'})}
            dataIndex="createdOn"
            key="createOn"
            className="overview-table__createdOn"
            render={(text) => <DateFormat date={text} layout="dateTimeShort"/>}
            filterDropdown={(filter) => (
              <FilterDate apiKeyStart="createdOnFrom" apiKeyEnd="createdOnTo" contentUpdater={contentUpdater} filter={filter}/>
            )}
            filterIcon={() => {
              const isFiltered = (typeof filters.createdOnFrom === 'string' && filters.createdOnFrom.length > 0) || (typeof filters.createdOnTo === 'string' && filters.createdOnTo.length > 0);
              return <Icon type='filter' theme={isFiltered ? 'filled' : 'outlined'} style={{color: isFiltered ? '#1C69D4' : '#BFBFBF'}}/>;
            }}
          />
        )}
        <Column
          title={intl.formatMessage({id: 'tasksTable.dates'})}
          dataIndex="interventionDate"
          key="dates"
          className="overview-table__dates"
          filterDropdown={(filter) => (
            <FilterDate apiKeyStart="interventionStart" apiKeyEnd="interventionEnd" contentUpdater={contentUpdater} filter={filter}/>
          )}
          filterIcon={() => {
            const isFiltered = (typeof filters.interventionStart === 'string' && filters.interventionStart.length > 0) || (typeof filters.interventionEnd === 'string' && filters.interventionEnd.length > 0);
            return <Icon type='filter' theme={isFiltered ? 'filled' : 'outlined'} style={{color: isFiltered ? '#1C69D4' : '#BFBFBF'}}/>;
          }}
          render={(_, record) => (
            <InterventionDate
              interventionStart={record.interventionStart}
              interventionEnd={record.interventionEnd}
              properties={record.properties}
            />
          )}
        />
        <Column
          title={intl.formatMessage({id: 'tasksTable.customerCar'})}
          dataIndex="customerCar"
          key="customerCar"
          className="overview-table__customer-car"
          filterDropdown={(filter) => {
            const brandFilterOptions = Constants.BRANDS.map(b => ({
              label: intl.formatMessage(b.label),
              value: b.value
            }));
            return <FilterCheckbox apiKey="carBrand" contentUpdater={contentUpdater} options={brandFilterOptions} filter={filter}/>;
          }}
          filterIcon={() => {
            const isFiltered = filters.carBrand && filters.carBrand.length > 0;
            return <Icon type='filter' theme={isFiltered ? 'filled' : 'outlined'} style={{color: isFiltered ? '#1C69D4' : '#BFBFBF'}}/>;
          }}
          render={(_, record) => (
            <InterventionCustomer
              firstName={record.customerFirstname}
              lastName={record.customerLastname}
              carBrand={record.carBrand}
              carModel={record.carModel}
            />
          )}
        />
        <Column
          title={`${intl.formatMessage({id: 'vehicle.vin'})} & ${intl.formatMessage({id: 'vehicle.plate'})}`}
          dataIndex="vinPlate"
          key="vinPlate"
          className="overview-table__vin-plate"
          render={(_, record) => <InterventionVehicle carVin={record.carVin} carPlate={record.carPlate}/>}
        />
        {!User.isCallCenterAgent() && (
          <Column
            title={intl.formatMessage({id: 'tasksTable.advisor'})}
            dataIndex="advisor"
            key="advisor"
            className="overview-table__advisor"
            filterDropdown={(filter) => (
              <FilterCheckbox
                apiKey="advisor"
                options={parseAdvisorsToFilterLabel(distinctAdvisors && distinctAdvisors.body)}
                contentUpdater={contentUpdater}
                filter={filter}
                emptyValueFilter
              />
            )}
            filterIcon={() => {
              const isFiltered = filters.advisor && filters.advisor.length > 0;
              return <Icon type='filter' theme={isFiltered ? 'filled' : 'outlined'} style={{color: isFiltered ? '#1C69D4' : '#BFBFBF'}}/>;
            }}
          />
        )}
        <Column
          title={intl.formatMessage({id: 'tasksTable.type'})}
          dataIndex="type"
          key="type"
          className="overview-table__type"
          filterDropdown={(filter) => {
            const typeFilterOptions = Constants.TYPES.map(t => ({
              label: intl.formatMessage(t.label),
              value: t.value
            }));
            return <FilterCheckbox apiKey="type" options={typeFilterOptions} contentUpdater={contentUpdater} filter={filter}/>;
          }}
          filterIcon={() => {
            const isFiltered = filters.type && filters.type.length > 0;
            return <Icon type='filter' theme={isFiltered ? 'filled' : 'outlined'} style={{color: isFiltered ? '#1C69D4' : '#BFBFBF'}}/>;
          }}
          render={text => <JourneyType type={text}/>}
        />
        {!User.isCallCenterAgent() && (
          <Column
            title={intl.formatMessage({id: 'tasksTable.origin'})}
            dataIndex="origin"
            key="origin"
            className="overview-table__type"
            filterDropdown={(filter) => {
              const originFilterOptions = Constants.ORIGINS.map(o => ({
                label: intl.formatMessage(o.label),
                value: o.value
              }));
              return <FilterCheckbox apiKey="origins" options={originFilterOptions} contentUpdater={contentUpdater} filter={filter}/>;
            }}
            filterIcon={() => {
              const isFiltered = filters.origins && filters.origins.length > 0;
              return <Icon type='filter' theme={isFiltered ? 'filled' : 'outlined'} style={{color: isFiltered ? '#1C69D4' : '#BFBFBF'}}/>;
            }}
            render={(text, intervention) => (
              intl.formatMessage({id: `intervention.origin.${Intervention.getDisplayOrigin(text, intervention.carBrand)}`})
            )}
          />
        )}
        <Column
          title=""
          dataIndex="properties"
          key="properties"
          className="overview-table__fl-247 txtcenter"
          render={(properties, record) => (
            <InterventionType journey={{properties}} fleetback={record.fleetbackIntervention}/>
          )}
        />
        {!User.isCallCenterAgent() && (
          <Column
            title=""
            dataIndex="lastMessageHandled"
            key="messageStatus"
            className="overview-table__messages txtcenter"
            render={lastMessageHandled => <MessageStatus isActive={!lastMessageHandled}/>}
          />
        )}
      </Table>
    </div>
  );
};

TasksTable.propTypes = {
  content: PropTypes.array,
  loading: PropTypes.func
};

const mapStateToProps = state => ({
  dealers: getDealersIdsAndName(state),
  fetchInterventions: state.fetchInterventions,
  fetchDistinctAdvisors: state.fetchDistinctAdvisors,
  filters: getFilters(state)
});

const mapDispatchToProps = dispatch => ({
  dispatchFetchAdvisors: () => dispatch({type: 'FETCH_DISTINCT_ADVISORS.REQUEST'}),
  dispatchFetchDealers: () => dispatch(fetchDealers()),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(TasksTable));
